import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { CheckIcon, ChevronRightIcon, CircleIcon } from "lucide-react";

import { cn } from "@/lib/utils";

function DropdownMenu({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

function DropdownMenuPortal({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>) {
  return (
    <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />
  );
}

function DropdownMenuTrigger({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  return (
    <DropdownMenuPrimitive.Trigger
      data-slot="dropdown-menu-trigger"
      {...props}
    />
  );
}

function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={cn(
          "aero:bg-popover aero:text-black aero:data-[state=open]:animate-in aero:data-[state=closed]:animate-out aero:data-[state=closed]:fade-out-0 aero:data-[state=open]:fade-in-0 aero:data-[state=closed]:zoom-out-95 aero:data-[state=open]:zoom-in-95 aero:data-[side=bottom]:slide-in-from-top-2 aero:data-[side=left]:slide-in-from-right-2 aero:data-[side=right]:slide-in-from-left-2 aero:data-[side=top]:slide-in-from-bottom-2 aero:z-50 aero:max-h-(--radix-dropdown-menu-content-available-height) aero:min-w-[8rem] aero:origin-(--radix-dropdown-menu-content-transform-origin) aero:overflow-x-hidden aero:overflow-y-auto aero:!rounded-md aero:border aero:p-1 aero:shadow-md",
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

function DropdownMenuGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  return (
    <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
  );
}

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
  variant?: "default" | "destructive";
}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "aero:focus:bg-border/40 aero:focus:text-accent-foreground aero:data-[variant=destructive]:text-destructive aero:data-[variant=destructive]:focus:bg-destructive/10 aero:dark:data-[variant=destructive]:focus:bg-destructive/20 aero:data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!aero:text-destructive aero:[&_svg:not([class*='text-'])]:text-muted-foreground aero:relative aero:flex aero:cursor-default aero:items-center aero:gap-2 aero:!rounded-sm aero:px-2 aero:py-1.5 aero:text-sm aero:outline-hidden aero:select-none aero:data-[disabled]:pointer-events-none aero:data-[disabled]:opacity-50 aero:data-[inset]:pl-8 aero:[&_svg]:pointer-events-none aero:[&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "aero:focus:bg-accent aero:focus:text-accent-foreground aero:relative aero:flex aero:cursor-default aero:items-center aero:gap-2 aero:!rounded-sm aero:py-1.5 aero:pr-2 aero:pl-8 aero:text-sm aero:outline-hidden aero:select-none aero:data-[disabled]:pointer-events-none aero:data-[disabled]:opacity-50 aero:[&_svg]:pointer-events-none aero:[&_svg]:shrink-0 aero:[&_svg:not([class*='size-'])]:size-4",
        className
      )}
      checked={checked}
      {...props}
    >
      <span className="aero:pointer-events-none aero:absolute aero:left-2 aero:flex aero:size-3.5 aero:items-center aero:justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon className="aero:size-4" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

function DropdownMenuRadioGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
  return (
    <DropdownMenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      {...props}
    />
  );
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "aero:focus:bg-accent aero:focus:text-accent-foreground aero:relative aero:flex aero:cursor-default aero:items-center aero:gap-2 aero:!rounded-sm aero:py-1.5 aero:pr-2 aero:pl-8 aero:text-sm aero:outline-hidden aero:select-none aero:data-[disabled]:pointer-events-none aero:data-[disabled]:opacity-50 aero:[&_svg]:pointer-events-none aero:[&_svg]:shrink-0 aero:[&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <span className="aero:pointer-events-none aero:absolute aero:left-2 aero:flex aero:size-3.5 aero:items-center aero:justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <CircleIcon className="aero:size-2 aero:fill-current" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
}) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        "aero:px-2 aero:py-1.5 aero:text-sm aero:font-medium aero:data-[inset]:pl-8",
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn("aero:bg-border aero:-mx-1 aero:my-1 aero:h-px", className)}
      {...props}
    />
  );
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        "aero:text-muted-foreground aero:ml-auto aero:text-xs aero:tracking-widest",
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuSub({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
  return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />;
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean;
}) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "aero:focus:bg-accent aero:focus:text-accent-foreground aero:data-[state=open]:bg-accent aero:data-[state=open]:text-accent-foreground aero:flex aero:cursor-default aero:items-center aero:!rounded-sm aero:px-2 aero:py-1.5 aero:text-sm aero:outline-hidden aero:select-none aero:data-[inset]:pl-8",
        className
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="aero:ml-auto aero:size-4" />
    </DropdownMenuPrimitive.SubTrigger>
  );
}

function DropdownMenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    <DropdownMenuPrimitive.SubContent
      data-slot="dropdown-menu-sub-content"
      className={cn(
        "aero:bg-popover aero:text-popover-foreground aero:data-[state=open]:animate-in aero:data-[state=closed]:animate-out aero:data-[state=closed]:fade-out-0 aero:data-[state=open]:fade-in-0 aero:data-[state=closed]:zoom-out-95 aero:data-[state=open]:zoom-in-95 aero:data-[side=bottom]:slide-in-from-top-2 aero:data-[side=left]:slide-in-from-right-2 aero:data-[side=right]:slide-in-from-left-2 aero:data-[side=top]:slide-in-from-bottom-2 aero:z-50 aero:min-w-[8rem] aero:origin-(--radix-dropdown-menu-content-transform-origin) aero:overflow-hidden aero:!rounded-md aero:border aero:p-1 aero:shadow-lg",
        className
      )}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
